// /app/frontend/src/pages/compliance/questionnaire/CampaignDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Users, CheckCircle2, Clock, AlertTriangle,
  BarChart3, Eye, Copy, RefreshCw, Lock, FileText, ChevronDown,
  ChevronUp, Settings, Target, X, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../../../components/ui/collapsible';

import { useAuth } from '../../../contexts/AuthContext';
import { questionnaireAPI } from '../../../services/api';

const STATUS_BADGES = {
  draft: { label: 'Draft', variant: 'secondary' },
  scheduled: { label: 'Scheduled', variant: 'outline' },
  sent: { label: 'Sent', variant: 'default' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  closed: { label: 'Closed', variant: 'secondary' },
  finalized: { label: 'Finalized', variant: 'success' },
};

const RECIPIENT_STATUS_BADGES = {
  pending: { label: 'Pending', variant: 'secondary' },
  not_started: { label: 'Not Started', variant: 'outline' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  submitted: { label: 'Submitted', variant: 'success' },
};

const DOMAIN_INFO = {
  GOV: { name: 'Governance & Accountability', color: 'bg-blue-500' },
  RCT: { name: 'Rights & Consent Management', color: 'bg-green-500' },
  DLM: { name: 'Data Lifecycle Management', color: 'bg-purple-500' },
  SEC: { name: 'Security & Access Controls', color: 'bg-orange-500' },
  TPM: { name: 'Third-Party Management', color: 'bg-cyan-500' },
  INC: { name: 'Incident Management', color: 'bg-red-500' },
};

// v2.0 Improvement 4: domain heatmap. Ordered left→right to match other module views.
const HEATMAP_DOMAINS = ['GOV', 'RCT', 'DLM', 'SEC', 'TPM', 'INC'];

// Heatmap cell styles by severity band (proportion of flagged questions in the domain).
const HEATMAP_BAND_STYLES = {
  none: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-300',
  low: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-300',
  high: 'bg-red-50 border-red-300 text-red-900 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300',
};

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, isDPO, isComplianceReadOnly: isReadOnly } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [score, setScore] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [responsesByQuestion, setResponsesByQuestion] = useState([]);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const [activeTab, setActiveTab] = useState('overview');
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendConflicts, setSendConflicts] = useState([]);
  const [checkingPreflight, setCheckingPreflight] = useState(false);
  const [recipientToRemove, setRecipientToRemove] = useState(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [overrideData, setOverrideData] = useState({ final_answer_key: '', reason: '' });

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const [campaignRes, scoreRes] = await Promise.all([
        questionnaireAPI.getCampaign(campaignId),
        questionnaireAPI.getScore('campaign', campaignId).catch(() => ({ data: null }))
      ]);
      
      setCampaign(campaignRes.data);
      setScore(scoreRes.data);
      
      // Load reconciliation status if DPO
      if (isDPO && campaignRes.data.recipient_stats?.submitted > 0) {
        const [reconRes, responsesRes] = await Promise.all([
          questionnaireAPI.getReconciliationStatus(campaignId),
          questionnaireAPI.getResponsesByQuestion(campaignId)
        ]);
        setReconciliation(reconRes.data);
        setResponsesByQuestion(responsesRes.data);
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSendDialog = async () => {
    try {
      setCheckingPreflight(true);
      const res = await questionnaireAPI.preflightCampaign(campaignId);
      setSendConflicts(res.data.conflicts || []);
      setShowSendDialog(true);
    } catch (error) {
      console.error('Preflight check failed:', error);
      toast.error('Failed to check recipient status');
    } finally {
      setCheckingPreflight(false);
    }
  };

  const handleSend = async () => {
    try {
      await questionnaireAPI.sendCampaign(campaignId);
      toast.success('Campaign sent! Guest accounts are being provisioned and invite emails sent.');
      setShowSendDialog(false);
      setSendConflicts([]);
      loadCampaign();
    } catch (error) {
      console.error('Error sending campaign:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to send campaign');
    }
  };

  const handleRemoveRecipient = async () => {
    if (!recipientToRemove) return;
    try {
      await questionnaireAPI.removeRecipient(campaignId, recipientToRemove.id);
      toast.success('Recipient removed');
      setRecipientToRemove(null);
      loadCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove recipient');
      setRecipientToRemove(null);
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await questionnaireAPI.sendReminders(campaignId);
      if (response.data.status === 'no_reminders_needed') {
        toast.info('All recipients have already submitted their responses.');
      } else {
        toast.success(`Reminder emails queued for ${response.data.recipients_count} recipients`);
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    }
  };

  const handleClose = async () => {
    try {
      await questionnaireAPI.closeCampaign(campaignId);
      toast.success('Campaign closed');
      setShowCloseDialog(false);
      loadCampaign();
    } catch (error) {
      console.error('Error closing campaign:', error);
      toast.error('Failed to close campaign');
    }
  };

  const handleFinalize = async () => {
    try {
      setIsFinalizing(true);
      await questionnaireAPI.finalizeCampaign(campaignId);
      toast.success('Campaign finalized successfully! SAQ suggestions generated.');
      setShowFinalizeDialog(false);
      loadCampaign();
    } catch (error) {
      console.error('Error finalizing campaign:', error);
      toast.error('Failed to finalize campaign');
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleCreateOverride = async () => {
    if (!selectedQuestion || !overrideData.final_answer_key || !overrideData.reason) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      const answerOption = campaign.template?.answer_options?.find(o => o.key === overrideData.final_answer_key);
      await questionnaireAPI.createOverride(campaignId, {
        campaign_id: campaignId,
        question_id: selectedQuestion.question_id,
        final_answer_key: overrideData.final_answer_key,
        final_answer_label: answerOption?.label || '',
        final_score_value: answerOption?.score || 0,
        reason: overrideData.reason
      });
      
      toast.success('Override created');
      setShowOverrideDialog(false);
      setSelectedQuestion(null);
      setOverrideData({ final_answer_key: '', reason: '' });
      loadCampaign();
    } catch (error) {
      console.error('Error creating override:', error);
      toast.error('Failed to create override');
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/questionnaire/respond/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!campaign) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <p className="text-muted-foreground">Campaign not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const submittedCount = campaign.recipient_stats?.submitted || 0;
  const totalRecipients = campaign.total_recipients || 0;
  const progressPercent = totalRecipients > 0 ? Math.round((submittedCount / totalRecipients) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="campaign-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/questionnaires')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{campaign.name}</h1>
              <Badge variant={STATUS_BADGES[campaign.status]?.variant}>
                {STATUS_BADGES[campaign.status]?.label}
              </Badge>
              {campaign.needs_reconciliation && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Needs Reconciliation
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{campaign.description || campaign.template?.title}</p>
          </div>
          <div className="flex gap-2">
            {!isReadOnly && campaign.status === 'draft' && (
              <Button onClick={handleOpenSendDialog} disabled={checkingPreflight}>
                {checkingPreflight
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Send className="h-4 w-4 mr-2" />}
                Send Campaign
              </Button>
            )}
            {!isReadOnly && ['sent', 'in_progress'].includes(campaign.status) && (
              <>
                <Button variant="outline" onClick={handleSendReminders}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Send Reminders
                </Button>
                <Button variant="outline" onClick={() => setShowCloseDialog(true)}>
                  <Lock className="h-4 w-4 mr-2" />
                  Close
                </Button>
              </>
            )}
            {!isReadOnly && campaign.status === 'closed' && isDPO && (
              <Button onClick={() => setShowFinalizeDialog(true)}>
                <Target className="h-4 w-4 mr-2" />
                Finalize
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Recipients</p>
                  <p className="text-2xl font-bold">{totalRecipients}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Submitted</p>
                  <p className="text-2xl font-bold text-green-600">{submittedCount}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-orange-600">{campaign.recipient_stats?.in_progress || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Campaign Score</p>
                  <p className="text-2xl font-bold">
                    {score?.campaign_score ? `${score.campaign_score.toFixed(1)}%` : '-'}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Response Progress</span>
              <span className="text-sm text-muted-foreground">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="recipients">Recipients ({totalRecipients})</TabsTrigger>
            {isDPO && submittedCount > 0 && (
              <TabsTrigger value="responses">Responses</TabsTrigger>
            )}
            {isDPO && reconciliation?.needs_reconciliation && (
              <TabsTrigger value="reconciliation" className="gap-1">
                <AlertTriangle className="h-4 w-4" />
                Reconciliation
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {score?.domain_scores && (
              <Card>
                <CardHeader>
                  <CardTitle>Domain Scores</CardTitle>
                  <CardDescription>Average scores across all submitted responses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(score.domain_scores).map(([code, data]) => {
                      const domain = DOMAIN_INFO[code] || { name: code, color: 'bg-gray-500' };
                      return (
                        <div key={code} className="p-4 border rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${domain.color}`} />
                            <span className="text-sm font-medium">{domain.name}</span>
                          </div>
                          <p className="text-2xl font-bold">{data.score?.toFixed(1)}%</p>
                          <p className="text-xs text-muted-foreground">
                            {data.respondent_count || 0} respondents
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Campaign Details */}
            <Card>
              <CardHeader>
                <CardTitle>Campaign Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <span className="ml-2">{new Date(campaign.created_at).toLocaleString()}</span>
                  </div>
                  {campaign.sent_at && (
                    <div>
                      <span className="text-muted-foreground">Sent:</span>
                      <span className="ml-2">{new Date(campaign.sent_at).toLocaleString()}</span>
                    </div>
                  )}
                  {campaign.closed_at && (
                    <div>
                      <span className="text-muted-foreground">Closed:</span>
                      <span className="ml-2">{new Date(campaign.closed_at).toLocaleString()}</span>
                    </div>
                  )}
                  {campaign.finalized_at && (
                    <div>
                      <span className="text-muted-foreground">Finalized:</span>
                      <span className="ml-2">{new Date(campaign.finalized_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recipients Tab */}
          <TabsContent value="recipients">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaign.recipients?.map(recipient => (
                      <TableRow key={recipient.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{recipient.name || 'Unknown'}</p>
                            <p className="text-sm text-muted-foreground">{recipient.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{recipient.department || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={RECIPIENT_STATUS_BADGES[recipient.status]?.variant}>
                            {RECIPIENT_STATUS_BADGES[recipient.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="w-20">
                            <Progress value={recipient.progress_percent || 0} className="h-2" />
                          </div>
                        </TableCell>
                        <TableCell>
                          {recipient.computed_score 
                            ? `${recipient.computed_score.toFixed(1)}%` 
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {recipient.submitted_at 
                            ? new Date(recipient.submitted_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyInviteLink(recipient.invitation_token)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            {!isReadOnly && campaign.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setRecipientToRemove(recipient)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Responses Tab (DPO only) */}
          <TabsContent value="responses">
            <Card>
              <CardHeader>
                <CardTitle>Responses by Question</CardTitle>
                <CardDescription>View all individual responses for reconciliation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {responsesByQuestion.map(question => (
                  <Collapsible key={question.question_id}>
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{question.domain_code}</Badge>
                            {question.needs_reconciliation && (
                              <Badge variant="destructive" className="text-xs">Requires Review</Badge>
                            )}
                            {question.divergence >= 2 && (
                              <Badge variant="warning" className="text-xs">High Divergence</Badge>
                            )}
                          </div>
                          <p className="text-sm">{question.question_text}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-medium">{question.avg_score?.toFixed(1) || '-'}</p>
                            <p className="text-xs text-muted-foreground">avg score</p>
                          </div>
                          <ChevronDown className="h-5 w-5" />
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 border-x border-b rounded-b-lg bg-muted/30">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Respondent</TableHead>
                              <TableHead>Department</TableHead>
                              <TableHead>Answer</TableHead>
                              <TableHead>Score</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {question.responses?.map((response, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{response.recipient_name}</TableCell>
                                <TableCell>{response.recipient_department || '-'}</TableCell>
                                <TableCell>{response.answer_label}</TableCell>
                                <TableCell>{response.score_value}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {!isReadOnly && isDPO && campaign.status !== 'finalized' && (
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedQuestion(question);
                                setShowOverrideDialog(true);
                              }}
                            >
                              Set Override
                            </Button>
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reconciliation Tab */}
          <TabsContent value="reconciliation">
            <Card>
              <CardHeader>
                <CardTitle>Reconciliation Required</CardTitle>
                <CardDescription>
                  The following items have significant response divergence or require DPO review
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* v2.0 Improvement 4: divergence heatmap — 6 cells, one per TR²UST domain.
                    Severity = flagged / total questions within the domain.
                    Colour bands: none flagged = green, ≤33% = amber, >33% = red. */}
                {(() => {
                  const flagged = reconciliation?.divergence?.flagged_questions || [];
                  const questions = campaign?.questions || [];
                  const totalsByDomain = questions.reduce((acc, q) => {
                    acc[q.domain_code] = (acc[q.domain_code] || 0) + 1;
                    return acc;
                  }, {});
                  const flaggedByDomain = flagged.reduce((acc, q) => {
                    acc[q.domain_code] = (acc[q.domain_code] || 0) + 1;
                    return acc;
                  }, {});

                  const bandFor = (f, t) => {
                    if (!t || f === 0) return 'none';
                    const ratio = f / t;
                    return ratio > 0.33 ? 'high' : 'low';
                  };

                  return (
                    <div className="mb-2">
                      <div className="flex items-baseline justify-between mb-2">
                        <h4 className="text-sm font-semibold">Domain divergence heatmap</h4>
                        <span className="text-xs text-muted-foreground">flagged / total questions per domain</span>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {HEATMAP_DOMAINS.map(code => {
                          const total = totalsByDomain[code] || 0;
                          const fcount = flaggedByDomain[code] || 0;
                          const band = bandFor(fcount, total);
                          return (
                            <div
                              key={code}
                              className={`rounded-md border px-3 py-2 ${HEATMAP_BAND_STYLES[band]}`}
                              title={`${DOMAIN_INFO[code]?.name || code} — ${fcount} of ${total} flagged`}
                            >
                              <div className="text-xs font-semibold tracking-wide">{code}</div>
                              <div className="text-lg font-bold leading-tight">{fcount}/{total}</div>
                              <div className="text-[11px] opacity-80">flagged</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {reconciliation?.divergence?.flagged_questions?.map(q => (
                  <div key={q.question_id} className="p-4 border rounded-lg border-orange-300 bg-orange-50 dark:bg-orange-950/20">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="destructive">Question Divergence</Badge>
                      <span className="text-sm text-muted-foreground">
                        Range: {q.min_score} - {q.max_score} (diff: {q.divergence})
                      </span>
                    </div>
                    <p className="text-sm mb-2">{q.question_text}</p>
                    {!isReadOnly && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const full = responsesByQuestion.find(r => r.question_id === q.question_id);
                          setSelectedQuestion(full || q);
                          setShowOverrideDialog(true);
                        }}
                      >
                        Review & Override
                      </Button>
                    )}
                  </div>
                ))}

                {/* Existing Overrides */}
                {campaign.overrides?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Applied Overrides</h4>
                    {campaign.overrides.map(override => (
                      <div key={override.id} className="p-3 border rounded-lg mb-2 bg-green-50 dark:bg-green-950/20">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{override.question_id}</span>
                          <Badge variant="success">Overridden: {override.final_score_value}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          By {override.set_by_name}: {override.reason}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Dialog */}
      <AlertDialog open={showSendDialog} onOpenChange={(o) => { setShowSendDialog(o); if (!o) setSendConflicts([]); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {sendConflicts.length > 0
                ? <><AlertTriangle className="h-5 w-5 text-destructive" /> Cannot Send Campaign</>
                : 'Send Campaign?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {sendConflicts.length > 0 ? (
                  <>
                    <p>
                      The following {sendConflicts.length === 1 ? 'recipient has' : 'recipients have'} an
                      active guest session and must be revoked before this campaign can be sent:
                    </p>
                    <ul className="rounded-md border border-destructive/30 bg-destructive/5 divide-y divide-destructive/10">
                      {sendConflicts.map(c => (
                        <li key={c.email} className="px-3 py-2 text-sm">
                          <span className="font-medium">{c.full_name || c.email}</span>
                          <span className="text-muted-foreground ml-2">{c.email}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            (active: {c.current_modules?.join(', ')})
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-muted-foreground">
                      Go to <strong>Temp Guest Management</strong> to revoke their access, then try sending again.
                    </p>
                  </>
                ) : (
                  <p>
                    This will provision guest accounts for all {totalRecipients} recipients and send
                    them login credentials via email. They will log in at the guest portal to complete
                    the assessment.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {sendConflicts.length === 0 && (
              <AlertDialogAction onClick={handleSend}>Send</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close Dialog */}
      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent any new submissions. Recipients who haven't submitted
              will no longer be able to respond.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose}>Close Campaign</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Finalize Dialog */}
      <AlertDialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog && !isFinalizing}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalize Campaign Results?</AlertDialogTitle>
            <AlertDialogDescription>
              This will lock all responses, create the final scoring snapshot, and generate SAQ maturity suggestions.
              Processing may take 20-30 seconds. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isFinalizing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleFinalize} disabled={isFinalizing}>
              {isFinalizing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Finalizing... (generating suggestions)
                </>
              ) : (
                'Finalize'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Recipient Confirmation */}
      <AlertDialog open={!!recipientToRemove} onOpenChange={(open) => !open && setRecipientToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Recipient?</AlertDialogTitle>
            <AlertDialogDescription>
              {recipientToRemove && (
                <>Remove <strong>{recipientToRemove.name || recipientToRemove.email}</strong> ({recipientToRemove.email}) from this campaign? This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleRemoveRecipient}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Override</DialogTitle>
            <DialogDescription>
              Set a reconciled answer for this question. This will override the averaged response.
            </DialogDescription>
          </DialogHeader>
          {selectedQuestion && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                {selectedQuestion.question_text}
              </div>
              <div className="space-y-2">
                <Label>Final Answer</Label>
                <Select 
                  value={overrideData.final_answer_key} 
                  onValueChange={(v) => setOverrideData({ ...overrideData, final_answer_key: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select answer" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaign.template?.answer_options?.map(opt => (
                      <SelectItem key={opt.key} value={opt.key}>
                        {opt.label} ({opt.score})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason for Override *</Label>
                <Textarea
                  placeholder="Explain why this override is necessary..."
                  value={overrideData.reason}
                  onChange={(e) => setOverrideData({ ...overrideData, reason: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>Cancel</Button>
            {!isReadOnly && (
              <Button onClick={handleCreateOverride}>Create Override</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
